import React from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";

const faqs = [
  {
    q: "How do I know if I have bruxism?",
    a: "Bruxism rarely announces itself directly — it's usually flagged by symptoms: morning jaw soreness, tension headaches, worn or chipped teeth, disturbed sleep, or a partner mentioning grinding noises. If three or more feel familiar, it's worth a clinical assessment.",
  },
  {
    q: "What happens at the consultation?",
    a: "Dr. Patel will examine your jaw muscles, bite, tooth wear and TMJ, take photographs and digital scans where helpful, and discuss what's likely driving your symptoms. You'll receive a personalised plan via WhatsApp afterwards — with no obligation.",
  },
  {
    q: "Is the consultation really free?",
    a: "Yes — completely free, with no obligation. If we don't think we can help, we'll tell you. Honesty is core to how we practice.",
  },
  {
    q: "Will treatment change how my face looks?",
    a: "No — your expressions and smile aren't affected. Where the jaw muscles are over-developed from years of clenching, many patients notice a subtle softening of the lower face over time, which most see as a welcome side benefit.",
  },
  {
    q: "How quickly will I notice a difference?",
    a: "Most patients begin noticing relief within 5–10 days, with the full effect settling in by around two weeks. We always book a follow-up review at this point to fine-tune.",
  },
  {
    q: "How long do the results last?",
    a: "Typically several months. With repeated appointments, the underlying clenching pattern often retrains, so many patients find they need treatment less frequently over time.",
  },
  {
    q: "Are there any side effects?",
    a: "Side effects are rare and usually minor (such as mild tenderness or bruising at the treatment site). Dr. Patel will discuss everything in detail at your consultation, and you'll receive written aftercare guidance.",
  },
  {
    q: "Will I still need a night guard?",
    a: "Not necessarily. Many patients reduce or stop using their guard once their bruxism is well managed. We'll review this with you based on your tooth wear and clenching pattern.",
  },
  {
    q: "Can I exercise / fly / drink afterwards?",
    a: "You can drive home and continue your day. We just ask that you avoid massaging the area, lying flat, hot yoga or saunas for the first 4 hours.",
  },
];

const nesaFaqs = [
  {
    q: "Does NESA neuromodulation hurt?",
    a: "No. The micro-currents used by NESA® are sub-sensory — the vast majority of patients feel nothing at all during a session. Soft electrodes simply rest on the wrists and ankles; nothing pierces the skin.",
  },
  {
    q: "How quickly will I notice a difference?",
    a: "Many patients report calmer sleep and softer jaw tone within the first 2–4 sessions. Because NESA works through compounding effects (hormesis), the benefit deepens across the full 10-session course delivered over 4–5 weeks.",
  },
  {
    q: "Is NESA safe? Any contraindications?",
    a: "NESA® is non-invasive, drug-free and has no reported adverse side effects. However it isn't suitable for everyone — for example patients with implanted pacemakers, certain neurological devices, or who are pregnant. Suitability is always assessed at your free consultation.",
  },
  {
    q: "Can I combine NESA with my night guard?",
    a: "Yes — they target different mechanisms. A night guard protects the teeth from grinding contact; NESA helps calm the nervous system driving the grinding in the first place. Many patients use both during the early phase of treatment.",
  },
  {
    q: "Does NESA replace in-clinic bruxism treatment?",
    a: "It doesn't have to. NESA pairs especially well with our in-clinic bruxism programme: the clinical treatment calms the over-active muscle directly, while NESA settles the nervous system that's firing it. Dr. Patel will recommend the right combination for you.",
  },
];

export const FAQ = () => {
  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="py-24 lg:py-32 bg-rb-bg-2 border-y border-rb-border"
    >
      <div className="max-w-4xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-12">
          <div className="font-overline mb-4">Common questions</div>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-rb-text leading-[1.1]">
            Everything you might be wondering.
          </h2>
        </div>

        <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
          {faqs.map((f, i) => (
            <AccordionItem
              key={f.q}
              value={`item-${i}`}
              data-testid={`faq-item-${i}`}
              className="card-soft px-6 border"
            >
              <AccordionTrigger className="font-serif-display text-lg text-rb-text hover:no-underline py-5 text-left">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-rb-text-2 leading-relaxed pb-5">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        <div className="mt-14 mb-6 flex items-center gap-4">
          <span className="h-px flex-1 bg-rb-border" />
          <span
            className="font-overline inline-flex items-center gap-2"
            data-testid="faq-nesa-heading"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#93C5FD]" />
            About NESA neuromodulation
          </span>
          <span className="h-px flex-1 bg-rb-border" />
        </div>

        <Accordion
          type="single"
          collapsible
          className="space-y-3"
          data-testid="faq-nesa-accordion"
        >
          {nesaFaqs.map((f, i) => (
            <AccordionItem
              key={f.q}
              value={`nesa-item-${i}`}
              data-testid={`faq-nesa-item-${i}`}
              className="card-soft px-6 border"
            >
              <AccordionTrigger className="font-serif-display text-lg text-rb-text hover:no-underline py-5 text-left">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-rb-text-2 leading-relaxed pb-5">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};
